import React, { useEffect, useState } from "react";

import { getDocument, GlobalWorkerOptions } from "pdfjs-dist";
import type { PDFDocumentProxy } from "pdfjs-dist";
import dayjs from "utils/dayjs-config";
import { Spin } from "antd";

interface Props {
  url: string;
  children?: (pdfDocument: PDFDocumentProxy) => JSX.Element;
  onError?: (error: Error) => void;
  onExpiredUrl: () => void;
  cMapUrl?: string;
  cMapPacked?: boolean;
}

const workerSrc = "/scripts/pdf.worker.min.js";
GlobalWorkerOptions.workerSrc = workerSrc;

export function PdfLoader(props: Props) {
  const { url, children, onExpiredUrl, cMapUrl, cMapPacked, onError } = props;
  const [pdfDocument, setPdfDocument] = useState<PDFDocumentProxy | null>();
  const [error, setError] = useState<Error | null>();
  const documentRef = React.createRef<HTMLElement>();

  useEffect(() => {
    const isUrlExpired = () => {
      const urlParams = new URLSearchParams(url);
      const date = urlParams.get("X-Amz-Date");
      if (!date) return;
      const expireTime = urlParams.get("X-Amz-Expires");
      // AWS SigV4 emits X-Amz-Date as YYYYMMDDTHHmmssZ (no separators). Moment
      // used to parse this loosely but dayjs won't without an explicit format,
      // so parse in UTC with the AWS shape. Also note dayjs is immutable —
      // capture the .add() return (the pre-migration code relied on moment's
      // in-place mutation and was broken on dayjs).
      const dateTime = dayjs
        .utc(date, "YYYYMMDDTHHmmss[Z]")
        .add(Number(expireTime ?? 0), "seconds");
      const now = dayjs.utc();
      if (now > dateTime) {
        onExpiredUrl();
        return true;
      }
      return false;
    };
    const load = async () => {
      const { ownerDocument = document } = documentRef.current || {};
      const discardedDocument = pdfDocument;
      setPdfDocument(null);
      setError(null);
      if (isUrlExpired()) return;

      try {
        if (discardedDocument) {
          await discardedDocument.destroy();
        }
        if (!url) {
          return;
        }

        const doc = await getDocument({
          ...props,
          ownerDocument,
          cMapUrl,
          cMapPacked
        }).promise;
        setPdfDocument(doc);
      } catch (e) {
        if (onError) {
          onError(e as Error);
        }
        setPdfDocument(null);
        setError(e as Error);
      }
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url]);

  if (error) return <div>Error {error?.message}</div>;

  if (pdfDocument && children) {
    return (
      <>
        <span ref={documentRef} />
        {children(pdfDocument)}
      </>
    );
  }

  return (
    <div className="flex justify-center items-center h-full w-full">
      <Spin size="large" />
    </div>
  );
}

export default PdfLoader;
